import { useContext, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Flag, History } from 'lucide-react';
import DataTable from '../../components/tables/DataTable';
import StatusBadge from '../../components/StatusBadge';
import Tooltip from '../../components/Tooltip';
import OrderJourneyStepper from '../../components/OrderJourneyStepper';
import OrderTimeline from '../../components/OrderTimeline';
import DialogModal from '../../components/dialog';
import { MyContext } from '../../context';
import {
	getOrder,
	getOrderStatus,
	listCommerceEvents,
	replayOrder,
	submitOrderToPace,
	cancelOrder,
	simulateJobStatus,
	updateOrderShipDate,
	approveOrder,
	releaseOrder,
} from '../../utils/queries';
import { getPrimaryStatus, getJourneySteps } from '../../utils/orderStatus';
import { useCan } from '../../hooks/useCan';
import { ADMIN_ROLES, PLATFORMADMIN_ROLES } from '../../components/navConfig';
import { formatDate, formatShipDate, isWeekendDate, shipCalendarToday } from '../../utils/format';

const REPLAYABLE_STATES = ['VALIDATION_FAILED', 'ERP_FAILED', 'RETRYING'];
// Plain-language heading for the failure box under the journey stepper, one
// per REPLAYABLE_STATES entry (see orderStatus.js's ERP_FAILURE_LABELS for the
// matching short stepper/badge labels).
const ERP_FAILURE_HEADINGS = {
	VALIDATION_FAILED: 'Rejected before reaching Pace',
	ERP_FAILED: 'Pace submission failed',
	RETRYING: 'Last attempt failed — retrying automatically',
};

// The real payment method chosen at checkout -- order.payment_type only ever
// distinguishes CURRENCY/LOYALTY_POINTS (money vs. points), it can't tell
// credit card, invoice, and PO apart. See order.model.ts's payment_method column.
const PAYMENT_METHOD_LABELS = {
	LOYALTY_POINTS: 'Loyalty Points',
	CREDIT_CARD: 'Credit Card',
	PURCHASE_ORDER: 'Purchase Order',
	INVOICE: 'Invoice',
};

// How each line item is fulfilled -- order.fulfillments is attached only by the
// admin GET /admin/orders/:id (see adminOrders.controller.ts#getOrder), never
// the storefront order API, since custom_form_data can hold prescription details.
const FULFILLMENT_TYPE_LABELS = {
	PHOENIX_STANDARD: 'Phoenix Standard',
	PARTNER_LAB: 'Partner Lab',
};

const DELIVERY_METHOD_LABELS = {
	EMAIL_IMMEDIATE: 'Emailed on ERP acceptance',
	WEEKLY_EXPORT: 'Weekly export',
};

// custom_form_data names are the storefront's raw form keys
// (e.g. frame_manufacturer, sphere_r) -- turn them into readable labels,
// with a trailing _r / _l shown as (R) / (L).
function humanizeFieldName(name) {
	const match = String(name).match(/^(.*)_([rl])$/i);
	const base = match ? match[1] : String(name);
	const words = base
		.split('_')
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ');
	return match ? `${words} (${match[2].toUpperCase()})` : words;
}

// A flat list of everything the admin can manually assign to a test-mode
// order's job status, in one dropdown. Received/Sent to ERP are always already
// complete by the time a test order exists (Pace has no concept of them),
// so they're listed only so the dropdown mirrors the full lifecycle shown in
// the journey stepper, but paceJobStatus: null keeps them non-selectable.
// Back Order and Hold both keep the journey stepper pinned at "In Process"
// (see orderStatus.js's getJourneySteps) -- they're sub-reasons the job is
// sitting there, not a different point in the pipeline; they're surfaced
// instead via the header/list status badges and the raw "ERP Job Status"
// field. "Cancelled" delegates to the same cancelOrder flow as the
// standalone Cancel Order button (see erpSubmit.service.ts's simulateJobStatus).
const JOB_STATUS_OPTIONS = [
	{ key: 'received', label: 'Received', stepKey: 'received', paceJobStatus: null },
	{ key: 'submitted', label: 'Sent to ERP', stepKey: 'submitted', paceJobStatus: null },
	{ key: 'production', label: 'In Process', stepKey: 'production', paceJobStatus: 'O' },
	{ key: 'backorder', label: 'Back Order', stepKey: 'production', paceJobStatus: 'b' },
	{ key: 'hold', label: 'Hold', stepKey: 'production', paceJobStatus: 'W' },
	{ key: 'shipped', label: 'Shipped', stepKey: 'shipped', paceJobStatus: 'S' },
	{ key: 'cancelled', label: 'Cancelled', stepKey: 'cancelled', paceJobStatus: 'X' },
];

const STEP_PILL_STYLES = {
	idle: 'bg-gray-100 text-gray-500',
	working: 'bg-blue-50 text-blue-600',
	passed: 'bg-green-50 text-green-700',
	failed: 'bg-red-50 text-pi-red',
};
const STEP_PILL_LABELS = {
	idle: 'Not started',
	working: 'Working...',
	passed: 'Passed',
	failed: 'Failed',
};

function StepPill({ status }) {
	return (
		<span className={`inline-block shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STEP_PILL_STYLES[status]}`}>
			{STEP_PILL_LABELS[status]}
		</span>
	);
}

export default function OrderDetail() {
	const { id } = useParams();
	const navigate = useNavigate();
	const canResubmitToErp = useCan(PLATFORMADMIN_ROLES);
	const canEditShipDate = useCan(ADMIN_ROLES);
	// Same audience the API allows (orderApprovals.service.ts's
	// assertOrderApprovalPermission): store admins and designated approvers.
	const { activeStoreIsOrderApprover } = useContext(MyContext);
	const canApproveOrder = canEditShipDate || activeStoreIsOrderApprover;
	// Manual-review release is store-admin only (orderReview.service.ts via
	// adminOrders.controller.ts#releaseOrder's assertStoreAdmin).
	const canReleaseOrder = canEditShipDate;
	const orderId = parseInt(id, 10);
	const [order, setOrder] = useState(null);
	const [status, setStatus] = useState(null);
	const [events, setEvents] = useState([]);
	const [loading, setLoading] = useState(true);
	const [reason, setReason] = useState('');
	const [resendExpanded, setResendExpanded] = useState(false);
	const [recheckStatus, setRecheckStatus] = useState('idle');
	const [recheckMessage, setRecheckMessage] = useState('');
	const [step1Collapsed, setStep1Collapsed] = useState(false);
	const [sendStatus, setSendStatus] = useState('idle');
	const [sendMessage, setSendMessage] = useState('');
	const [cancelStatus, setCancelStatus] = useState('idle');
	const [shipDateEditing, setShipDateEditing] = useState(false);
	const [shipDateValue, setShipDateValue] = useState('');
	const [shipDateStatus, setShipDateStatus] = useState('idle');
	const [shipDateMessage, setShipDateMessage] = useState('');
	const [cancelMessage, setCancelMessage] = useState('');
	const [cancelConfirmOpen, setCancelConfirmOpen] = useState(false);
	const [approveConfirmOpen, setApproveConfirmOpen] = useState(false);
	const [approveStatus, setApproveStatus] = useState('idle');
	const [approveMessage, setApproveMessage] = useState('');
	const [releaseConfirmOpen, setReleaseConfirmOpen] = useState(false);
	const [releaseStatus, setReleaseStatus] = useState('idle');
	const [releaseMessage, setReleaseMessage] = useState('');
	const [cancelReason, setCancelReason] = useState('Cancelled by admin');
	const [jobStatusStatus, setJobStatusStatus] = useState('idle');
	const [jobStatusMessage, setJobStatusMessage] = useState('');
	const [jobStatusConfirmOpen, setJobStatusConfirmOpen] = useState(false);
	const [jobStatusValue, setJobStatusValue] = useState('production');
	const [timelineOpen, setTimelineOpen] = useState(false);
	const [formDetailsItem, setFormDetailsItem] = useState(null);

	const refresh = () => {
		setLoading(true);
		return Promise.all([
			getOrder(orderId),
			getOrderStatus(orderId),
			listCommerceEvents({ entityType: 'order', entityId: orderId }),
		]).then(([orderResult, statusResult, eventsResult]) => {
			if (orderResult?.success) setOrder(orderResult.data);
			if (statusResult?.success) setStatus(statusResult.data);
			if (eventsResult?.success) setEvents(eventsResult.data.events);
			setLoading(false);
		});
	};

	useEffect(() => {
		refresh();
		setReason('');
		setResendExpanded(false);
		setRecheckStatus('idle');
		setRecheckMessage('');
		setStep1Collapsed(false);
		setSendStatus('idle');
		setSendMessage('');
		setCancelStatus('idle');
		setCancelMessage('');
		setCancelConfirmOpen(false);
		setCancelReason('Cancelled by admin');
		setJobStatusStatus('idle');
		setJobStatusMessage('');
		setJobStatusConfirmOpen(false);
		setJobStatusValue('production');
		setTimelineOpen(false);
		setFormDetailsItem(null);
		setReleaseConfirmOpen(false);
		setReleaseStatus('idle');
		setReleaseMessage('');
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [orderId]);

	const handleRecheck = async (e) => {
		e.preventDefault();
		setRecheckStatus('working');
		setRecheckMessage('');
		const result = await replayOrder(orderId, reason);
		if (!result?.success) {
			setRecheckStatus('failed');
			setRecheckMessage(result?.error?.message || 'Recheck failed.');
			return;
		}
		// A 200/success response doesn't mean the replay itself passed -- the
		// API still returns success:true when replay validation fails again,
		// with the real outcome in data.state / data.validation_errors.
		const passed = result.data?.state !== 'VALIDATION_FAILED';
		if (!passed) {
			setRecheckStatus('failed');
			const errors = result.data?.validation_errors;
			setRecheckMessage(
				errors?.length ? errors.map((err) => err.error).join(' ') : result.data?.message || 'Recheck failed.'
			);
			refresh();
			return;
		}
		setRecheckStatus('passed');
		setRecheckMessage(result.data?.message || 'Recheck passed — ready to send.');
		setReason('');
		refresh();
	};

	const handleSend = async (checkOnly) => {
		setSendStatus('working');
		setSendMessage('');
		const mode = checkOnly
			? 'validate_only'
			: status.state === 'ERP_FAILED' || status.state === 'RETRYING'
				? 'retry_failed'
				: 'create_job';
		const result = await submitOrderToPace(orderId, mode);
		if (!result?.success) {
			setSendStatus('failed');
			setSendMessage(result?.error?.message || 'Failed.');
			return;
		}
		setSendStatus('passed');
		setSendMessage(checkOnly ? 'Validation passed.' : `Submitted to ERP (${result.data?.pace?.job || 'queued'}).`);
		refresh();
	};

	const handleCancelConfirm = async () => {
		setCancelConfirmOpen(false);
		setCancelStatus('working');
		setCancelMessage('');
		const result = await cancelOrder(orderId, cancelReason.trim() || 'Cancelled by admin');
		if (!result?.success) {
			setCancelStatus('failed');
			setCancelMessage(result?.error?.message || 'Cancel failed.');
			return;
		}
		setCancelStatus('passed');
		setCancelMessage(result.data?.message || 'Order cancelled.');
		refresh();
	};

	const handleApproveConfirm = async () => {
		setApproveConfirmOpen(false);
		setApproveStatus('working');
		setApproveMessage('');
		const result = await approveOrder(orderId);
		if (!result?.success) {
			setApproveStatus('failed');
			setApproveMessage(result?.error?.message || 'Approve failed.');
			return;
		}
		setApproveStatus('passed');
		// With manual review also on, approval hands off to PENDING_REVIEW
		// rather than READY_FOR_ERP (orderReview.service.ts#enterErpQueue).
		setApproveMessage(
			result.data?.state === 'PENDING_REVIEW'
				? 'Order approved. It now needs a manual review before it is sent to Pace.'
				: 'Order approved. It will be sent to Pace on the next submission run.',
		);
		refresh();
	};

	const handleReleaseConfirm = async () => {
		setReleaseConfirmOpen(false);
		setReleaseStatus('working');
		setReleaseMessage('');
		const result = await releaseOrder(orderId);
		if (!result?.success) {
			setReleaseStatus('failed');
			setReleaseMessage(result?.error?.message || 'Release failed.');
			return;
		}
		setReleaseStatus('passed');
		setReleaseMessage('Order released. It will be sent to Pace on the next submission run.');
		refresh();
	};

	const startShipDateEdit = () => {
		setShipDateValue(order?.anticipated_ship_date ? String(order.anticipated_ship_date).slice(0, 10) : '');
		setShipDateStatus('idle');
		setShipDateMessage('');
		setShipDateEditing(true);
	};

	const handleShipDateSave = async () => {
		if (!shipDateValue) return;
		setShipDateStatus('working');
		setShipDateMessage('');
		const result = await updateOrderShipDate(orderId, shipDateValue);
		if (!result?.success) {
			setShipDateStatus('failed');
			setShipDateMessage(result?.error?.message || 'Could not update the ship date.');
			return;
		}
		const { pace_sync: paceSync, warning } = result.data || {};
		const notes = [
			paceSync === 'updated' && 'Pace updated.',
			paceSync === 'failed' && 'Saved, but Pace was not updated — it will need changing in Pace.',
			warning === 'non-business day' && 'Note: that date is a weekend or holiday.',
		].filter(Boolean);
		setShipDateStatus(paceSync === 'failed' ? 'failed' : 'passed');
		setShipDateMessage(notes.join(' ') || 'Ship date updated.');
		setShipDateEditing(false);
		refresh();
	};

	const handleJobStatusConfirm = async () => {
		const paceJobStatus = JOB_STATUS_OPTIONS.find((o) => o.key === jobStatusValue)?.paceJobStatus;
		if (!paceJobStatus) return;
		setJobStatusConfirmOpen(false);
		setJobStatusStatus('working');
		setJobStatusMessage('');
		const result = await simulateJobStatus(orderId, paceJobStatus, 'Set via admin (test mode)');
		if (!result?.success) {
			setJobStatusStatus('failed');
			setJobStatusMessage(result?.error?.message || 'Set job status failed.');
			return;
		}
		setJobStatusStatus('passed');
		setJobStatusMessage(result.data?.message || 'Job status updated.');
		refresh();
	};

	if (loading) {
		return <div className='py-8 pr-8 text-sm text-gray-400'>Loading...</div>;
	}

	if (!order || !status) {
		return (
			<div className='py-8 pr-8'>
				<p className='text-sm text-gray-500'>Order not found.</p>
				<button type='button' className='btn-secondary mt-4' onClick={() => navigate('/orders')}>
					Back to All Orders
				</button>
			</div>
		);
	}

	const isLoyaltyOrder = order.payment_type === 'LOYALTY_POINTS';
	// Loyalty orders carry no money, so total_price / currency come back as
	// $0.00 / USD. Show the summed line-item points instead, as a whole number.
	const totalPoints = (order.line_items || []).reduce(
		(sum, item) => sum + Math.round(Number(item.point_cost) || 0) * (Number(item.quantity) || 0),
		0
	);

	const lineItemColumns = [
		{ key: 'title', header: 'Item' },
		{ key: 'variant_sku', header: 'SKU' },
		{ key: 'quantity', header: 'Qty' },
		isLoyaltyOrder
			? {
					key: 'point_cost',
					header: 'Points',
					align: 'right',
					render: (value) => `${Math.round(Number(value)).toLocaleString()} pts`,
				}
			: { key: 'price', header: 'Price', align: 'right', render: (value) => `$${value}` },
	];

	const fulfillmentGroups = Object.values(
		(order.fulfillments || []).reduce((groups, item) => {
			const type = item.fulfillment_type || 'PHOENIX_STANDARD';
			const key = `${type}|${item.partner_name || ''}|${item.delivery_method || ''}`;
			return {
				...groups,
				[key]: {
					type,
					partnerName: item.partner_name,
					deliveryMethod: item.delivery_method,
					items: [...(groups[key]?.items || []), item],
				},
			};
		}, {})
	);

	const journeyInput = {
		state: status.state,
		erpOrderNumber: order.erp_order_number,
		paceJobStatus: status.pace_job_status,
		shipped: status.fulfillment?.shipped,
		customFulfillmentOnly: status.custom_fulfillment_only,
	};
	const primaryStatus = getPrimaryStatus(journeyInput);
	const journeySteps = getJourneySteps(journeyInput);

	// Snapshotted on the order itself at ingest -- reflects whatever this
	// order's mode was when it was created, even if the store has since been
	// flipped to live (see storeMode.service.ts's isTestModeOrder).
	const isTestModeOrder = (order.mode || 'test') === 'test';
	const cancellable = status.state !== 'CANCELLED' && !status.fulfillment?.shipped;

	// An option is only offered in the "Set Job Status" dropdown if it's not
	// already behind the order (no going backward) and this tool actually has
	// a Pace code to drive it to (Received/Sent to ERP are always already complete
	// by the time a test order exists, so they never show as selectable).
	// Back Order/Hold share the "In Process" step's own gate (see
	// JOB_STATUS_OPTIONS' stepKey) since they're sub-reasons for that same
	// step, not steps of their own. "Cancelled" isn't a real journey step
	// (see orderStatus.js), so it mirrors the standalone Cancel Order
	// button's own gate instead.
	const isJobStatusOptionSelectable = (key) => {
		const option = JOB_STATUS_OPTIONS.find((o) => o.key === key);
		if (!option?.paceJobStatus) return false;
		if (option.stepKey === 'cancelled') return cancellable;
		const step = journeySteps.find((s) => s.key === option.stepKey);
		return step?.status !== 'complete';
	};

	// Terminal states -- once the order has shipped/closed or been cancelled
	// there's no further job status to simulate, so the "Update Job Status"
	// control is locked. primaryStatus already folds Pace's "closed" code and
	// the shipped fulfillment flag into the 'shipped' bucket (see orderStatus.js).
	const jobStatusLocked = primaryStatus.bucket === 'shipped' || primaryStatus.bucket === 'cancelled';

	// When the modal opens, preselect the status the order has actually
	// reached rather than always starting at "In Process" -- a shipped order
	// should show "Shipped" selected, not a step it's long past. If that
	// reached status isn't itself a valid move (it's already complete), fall
	// to the first status the admin could actually advance the order to.
	const reachedJobStatusKey = () => {
		const activeStep = journeySteps.find((s) => s.status !== 'complete');
		const lastCompleteStep = [...journeySteps].reverse().find((s) => s.status === 'complete');
		const stepKey = (activeStep || lastCompleteStep)?.key || 'production';
		return JOB_STATUS_OPTIONS.find((o) => o.stepKey === stepKey)?.key || 'production';
	};
	const defaultJobStatusKey = () => {
		const reached = reachedJobStatusKey();
		if (isJobStatusOptionSelectable(reached)) return reached;
		return JOB_STATUS_OPTIONS.find((o) => isJobStatusOptionSelectable(o.key))?.key || reached;
	};
	const openJobStatusModal = () => {
		setJobStatusValue(defaultJobStatusKey());
		setJobStatusConfirmOpen(true);
	};
	const needsResend = REPLAYABLE_STATES.includes(status.state);
	const recheckRequired = status.state === 'VALIDATION_FAILED';
	const sendUnlocked = !recheckRequired || recheckStatus === 'passed';
	const latestEvent = status.events?.[status.events.length - 1];
	const failureReason = latestEvent?.details;
	// Test-mode orders never actually reach Pace -- submitOrderToPace
	// short-circuits into simulateErpAcceptance for them (see
	// erpSubmit.service.ts's isTestModeOrder gate), so ERP_FAILED/RETRYING
	// can't happen here; the only failure a test order can hit is
	// VALIDATION_FAILED (recheckRequired), which is always a real data
	// problem (bad SKU mapping, missing store defaults, etc.) worth
	// flagging. But "retrying" it just fabricates the same simulated
	// acceptance rather than actually reaching an ERP, so the Retry
	// control -- not the alert itself -- is what's irrelevant here.
	const retryRelevant = !(isTestModeOrder && recheckRequired);

	// Here is the order's full lifecycle:
	// -----   			-------------------------------------------	
	// state			meaning	details
	// -----   			-------------------------------------------
	// RECEIVED			declared in the type but never actually written by appendOrderState anywhere in the codebase — order rows don't exist yet at this point, so it's not observable in practice	
	// INGESTED			raw payload stored	"Raw payload stored"
	// NORMALIZED		payload converted to the canonical internal order shape	"Canonical order written"
	// VALIDATION_FAILED	ERP-mapping validation (or loyalty validation) failed — a recoverable, fixable failure state	mapping error details / loyalty failure details
	// PENDING_LOYALTY_APPROVAL	a LOYALTY_POINTS order is waiting on loyalty-program approval before proceeding	—
	// READY_FOR_ERP	validation (and loyalty approval, if applicable) passed; queued for ERP submission	often "Replay: <reason>" when reached via manual replay
	// ERP_SUBMITTING	actively being submitted to Pace right now	—
	// ERP_ACCEPTED		Pace accepted the job and returned a job number — the storefront-facing "success" state	"Pace job <jobId>"
	// ERP_FAILED		submission reached Pace but Pace rejected/errored it	Pace error message (truncated to 255 chars)
	// RETRYING			a background retry of a failed submission is in progress	truncated retry message
	// CANCELLED		order was cancelled (admin-triggered or detected as cancelled on the Pace side)	cancellation reason/message
	
	return (
		<div className='py-8 pr-8 max-w-4xl'>
			<button
				type='button'
				className='text-sm text-gray-500 hover:text-black mb-2'
				onClick={() => navigate('/orders')}
			>
				&larr; All Orders
			</button>
			<div className='flex items-center justify-between mb-1'>
				<div className='flex items-center gap-3'>
					<h1 className='text-2xl font-light tracking-tight'>{order.order_number}</h1>
					{isTestModeOrder && (
						<span className='inline-block rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-xs font-medium'>
							TEST MODE
						</span>
					)}
					<StatusBadge status={primaryStatus.bucket} text={primaryStatus.text} />
				</div>
				<div className='flex items-center gap-2'>
					{cancellable && (
						<button
							type='button'
							className='btn-alert'
							disabled={cancelStatus === 'working'}
							onClick={() => setCancelConfirmOpen(true)}
						>
							{cancelStatus === 'working' ? 'Cancelling...' : 'Cancel Order'}
						</button>
					)}
				</div>
			</div>
			{/* <p className='text-sm text-gray-500 mb-1'>
				Store {status.store_code} &middot; Source Order ID {order.source_order_id}
			</p> */}
			{cancelMessage && (
				<p className={`text-xs mb-1 ${cancelStatus === 'failed' ? 'text-pi-red' : 'text-green-700'}`}>
					{cancelMessage}
				</p>
			)}
			{jobStatusMessage && (
				<p className={`text-xs mb-1 ${jobStatusStatus === 'failed' ? 'text-pi-red' : 'text-green-700'}`}>
					{jobStatusMessage}
				</p>
			)}

			{isTestModeOrder && status.state !== 'CANCELLED' && (
				<div className='mt-4 flex items-center justify-between gap-4 rounded-md bg-red-50 px-4 py-3 text-sm text-red-800'>
					<p>
						This is a test order - simulate the fulfillment process by manually assigning job statuses.
					</p>
					<button
						type='button'
						className='btn-secondary disabled:opacity-50 shrink-0'
						disabled={jobStatusStatus === 'working' || jobStatusLocked}
						title={jobStatusLocked ? 'This order has shipped/closed or been cancelled — no further job status to simulate.' : undefined}
						onClick={openJobStatusModal}
					>
						{jobStatusStatus === 'working' ? 'Setting status...' : 'Update Job Status'}
					</button>
				</div>
			)}

			<div className='mt-6 mb-8 max-w-xl'>
				<OrderJourneyStepper steps={journeySteps} />
			</div>

			{/*
				failureReason is getErrorMessage(error) from whatever exception Pace's SOAP call threw.
				failureReason in this file is just the details string of the most recent order_processing_states row — 
				shown to every viewer whenever the order's state is a submission failure (VALIDATION_FAILED / ERP_FAILED / RETRYING); the resend controls below stay admin-only.
				It's free text, not a fixed enum, but it always comes from one of these sources depending on which state produced it:

				State 1) VALIDATION_FAILED — one of:
					- Mapping errors joined with ; (from validateOrderMappings, customerMapping.service.ts):
					- No active store_pace_accounts row for this store.
					- No active store_defaults fallback row for this store.
					- No product_erp_mapping found for SKU <sku>
					- product_erp_mapping for SKU <sku> has no erp_inventory_item_id set.
					- Pace InventoryItem <id> (mapped from SKU <sku>) was not found in Pace.
					- No shipping_method_mappings found for code <code>
					- At least one shipping_lines entry is required.
					- No payment_term_mappings found for <terms>
					- Store does not accept credit card payments.
					- Loyalty-approval denial text (from processLoyaltyPayment), e.g.:
					- loyalty block is required for LOYALTY_POINTS orders.
					- Unknown loyalty program <program_code>
					- a denial reason built from insufficient points balance / other approval-check failures
				State 2) ERP_FAILED — raw upstream error text from the failed Pace SOAP call - whatever Pace/strong-soap threw.
				State 3) RETRYING — same idea: raw retry-attempt error message, truncated to 255 chars, from the worker (syncWorkers.ts:606).

				So there's no closed enum of failureReason values - it's always a diagnostic string, but which 
				category of message you get is determined by which of those three states produced the latest event.
			*/}
			{needsResend && (
				<div className='-mt-4 mb-8 max-w-xl rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-gray-700'>
					<p className='font-medium text-red-700'>{ERP_FAILURE_HEADINGS[status.state]}</p>
					{failureReason && <p className='mt-1'>{failureReason}</p>}
				</div>
			)}

			{/* Approve is offered here while the approval is PENDING (admins and
			approvers only); decline stays on the /orders list's "Needs approval"
			filter (/orders?view=approval). order.approval is spliced in by
			ingest.service.ts#buildOrderDetail (getOrderApprovalSummary) whenever
			this order ever went through the require_order_approval gate. */}
			{order.approval && (
				<div className='p-3 border mb-8'>
					<h2 className='text-base font-medium mb-3'>Order Approval</h2>
					<dl className='text-sm space-y-1'>
						<div className='flex justify-between'>
							<dt className='text-gray-500'>Status</dt>
							<dd>
								<StatusBadge
									status={order.approval.status === 'PENDING' ? 'approval' : order.approval.status === 'APPROVED' ? 'active' : 'cancelled'}
									text={order.approval.status === 'PENDING' ? 'Pending' : order.approval.status === 'APPROVED' ? 'Approved' : 'Declined'}
								/>
							</dd>
						</div>
						{order.approval.status !== 'PENDING' && (
							<>
								<div className='flex justify-between'><dt className='text-gray-500'>Decided By</dt><dd>{order.approval.decided_by_name || `User #${order.approval.decided_by_user_id}`}</dd></div>
								<div className='flex justify-between'><dt className='text-gray-500'>Decided Via</dt><dd>{order.approval.decided_via === 'EMAIL_LINK' ? 'Email Link' : 'Admin UI'}</dd></div>
								<div className='flex justify-between'><dt className='text-gray-500'>Decided At</dt><dd>{formatDate(order.approval.decided_at, { pad: false })}</dd></div>
								{order.approval.reason && (
									<div className='flex justify-between'><dt className='text-gray-500'>Reason</dt><dd className='text-right max-w-xs'>{order.approval.reason}</dd></div>
								)}
							</>
						)}
					</dl>
					{order.approval.status === 'PENDING' && canApproveOrder && (
						<div className='mt-3 flex items-center gap-3'>
							<button
								type='button'
								className='btn-primary-small'
								disabled={approveStatus === 'working'}
								onClick={() => setApproveConfirmOpen(true)}
							>
								{approveStatus === 'working' ? 'Approving…' : 'Mark Approved'}
							</button>
						</div>
					)}
					{approveMessage && (
						<p className={`mt-2 text-sm ${approveStatus === 'failed' ? 'text-pi-red' : 'text-gray-600'}`}>
							{approveMessage}
						</p>
					)}
				</div>
			)}

			{/* Manual-review gate (stores.require_order_review): the order waits
			here until a store admin releases it to READY_FOR_ERP. Rejecting is
			the regular Cancel action -- there is no review-specific decline. */}
			{(status?.state === 'PENDING_REVIEW' || releaseMessage) && (
				<div className='p-3 border mb-8'>
					<h2 className='text-base font-medium mb-3'>Manual Review</h2>
					{status?.state === 'PENDING_REVIEW' && (
						<p className='text-sm text-gray-600'>
							This order is waiting for a store admin to release it to Pace.
						</p>
					)}
					{status?.state === 'PENDING_REVIEW' && canReleaseOrder && (
						<div className='mt-3 flex items-center gap-3'>
							<button
								type='button'
								className='btn-primary-small'
								disabled={releaseStatus === 'working'}
								onClick={() => setReleaseConfirmOpen(true)}
							>
								{releaseStatus === 'working' ? 'Releasing…' : 'Release to Pace'}
							</button>
						</div>
					)}
					{releaseMessage && (
						<p className={`mt-2 text-sm ${releaseStatus === 'failed' ? 'text-pi-red' : 'text-gray-600'}`}>
							{releaseMessage}
						</p>
					)}
				</div>
			)}

			<div className='grid grid-cols-2 gap-8 mb-8'>
				<div className='p-3 border'>
					<div className='flex items-center justify-between mb-3'>
						<h2 className='text-base font-medium'>Order Details</h2>
						<button
							type='button'
							className='text-gray-500 hover:text-black'
							title='View Timeline'
							onClick={() => setTimelineOpen(true)}
						>
							<History className='size-4' />
						</button>
					</div>
					<dl className='text-sm space-y-1'>
						<div className='flex justify-between'><dt className='text-gray-500'>Order Date</dt><dd>{formatDate(order.order_date)}{order.order_date && ` ${new Date(order.order_date).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}`}</dd></div>
						<div className='flex justify-between items-start gap-3'>
							<dt className='text-gray-500'>Expected Ship Date</dt>
							<dd className='text-right'>
								{shipDateEditing ? (
									<span className='flex items-center gap-2'>
										<input
											type='date'
											className='form-input py-0.5'
											aria-label='Anticipated ship date'
											min={shipCalendarToday()}
											value={shipDateValue}
											onChange={(e) => setShipDateValue(e.target.value)}
										/>
										<button
											type='button'
											className='btn-primary-small'
											disabled={!shipDateValue || shipDateStatus === 'working'}
											onClick={handleShipDateSave}
										>
											{shipDateStatus === 'working' ? 'Saving…' : 'Save'}
										</button>
										<button
											type='button'
											className='text-gray-500 hover:text-black'
											disabled={shipDateStatus === 'working'}
											onClick={() => setShipDateEditing(false)}
										>
											Cancel
										</button>
									</span>
								) : (
									<span>
										{formatShipDate(order.anticipated_ship_date)}
										{order.anticipated_ship_date_source === 'MANUAL' && (
											<span className='ml-1 text-xs text-gray-400'>(edited)</span>
										)}
										{canEditShipDate && cancellable && (
											<button
												type='button'
												className='ml-2 text-xs text-gray-500 underline hover:text-black'
												onClick={startShipDateEdit}
											>
												Edit
											</button>
										)}
									</span>
								)}
								{shipDateEditing && isWeekendDate(shipDateValue) && (
									<span className='block text-xs text-amber-700 mt-1'>That date is a weekend.</span>
								)}
								{shipDateMessage && (
									<span
										className={`block text-xs mt-1 ${shipDateStatus === 'failed' ? 'text-pi-red' : 'text-gray-500'}`}
									>
										{shipDateMessage}
									</span>
								)}
							</dd>
						</div>
						<div className='flex justify-between'><dt className='text-gray-500'>Payment Type</dt><dd>{order.payment_method ? (PAYMENT_METHOD_LABELS[order.payment_method] || order.payment_method) : 'Unknown'}</dd></div>
						<div className='flex justify-between'><dt className='text-gray-500'>Financial Status</dt><dd>{order.financial_status || '-'}</dd></div>
						<div className='flex justify-between'><dt className='text-gray-500'>PO Number</dt><dd>{order.po_number || '-'}</dd></div>
					</dl>
				</div>

				<div className='p-3 border'>
					<h2 className='text-base font-medium mb-3'>Customer</h2>
					<dl className='text-sm space-y-1'>
						<div className='flex justify-between'>
							<dt className='text-gray-500'>Name</dt>
							<dd>
								{order.customer_name && order.customer_id ? (
									<Link to={`/customers/${order.customer_id}`} className='text-black font-medium hover:underline'>
										{order.customer_name}
									</Link>
								) : (
									order.customer_name || '-'
								)}
							</dd>
						</div>
						<div className='flex justify-between'><dt className='text-gray-500'>Company</dt><dd>{order.customer_company || '-'}</dd></div>
						<div className='flex justify-between'><dt className='text-gray-500'>Address 1</dt><dd>{order.shipping_address?.address1 || '-'}</dd></div>
						<div className='flex justify-between'><dt className='text-gray-500'>Address 2</dt><dd>{order.shipping_address?.address2 || '-'}</dd></div>
						<div className='flex justify-between'><dt className='text-gray-500'>City</dt><dd>{order.shipping_address?.city || '-'}</dd></div>
						<div className='flex justify-between'><dt className='text-gray-500'>State / Zip</dt><dd>{[order.shipping_address?.state, order.shipping_address?.zip].filter(Boolean).join(' / ') || '-'}</dd></div>
						<div className='flex justify-between'><dt className='text-gray-500'>Email</dt><dd>{order.customer_email || '-'}</dd></div>
					</dl>
				</div>
			</div>

			<div className='bg-gray-50 p-4 mb-8'>
				<h2 className='text-base font-medium mb-3'>Line Items</h2>
				<DataTable columns={lineItemColumns} data={order.line_items} emptyHeading='No line items' />

				<dl className='pt-6 text-sm border-t border-gray-200 w-1/2 ml-auto'>
					<div className='flex justify-between px-4 py-2'><dt className='text-gray-500'>Subtotal</dt><dd>{order.subtotal_price != null ? `${isLoyaltyOrder ? '' : '$'}${order.subtotal_price}` : '-'}</dd></div>
					{order.coupon_code && order.discount_amount != null && (
						<div className='flex justify-between px-4 py-2'>
							<dt className='text-gray-500'>Discount ({order.coupon_code})</dt>
							<dd>-{order.discount_unit === 'points' ? `${order.discount_amount} pts` : `$${order.discount_amount}`}</dd>
						</div>
					)}
					<div className='flex justify-between px-4 py-2'><dt className='text-gray-500'>Shipping</dt><dd>{order.total_shipping ? `${isLoyaltyOrder ? '' : '$'}${order.total_shipping}` : 'Free'}</dd></div>
					<div className='flex justify-between px-4 py-2'><dt className='text-gray-500'>Taxes</dt><dd>{order.total_tax != null ? `${isLoyaltyOrder ? '' : '$'}${order.total_tax}` : '-'}</dd></div>
					<div className='flex justify-between items-baseline px-4 py-3 border-t border-gray-200'>
						<dt className='font-semibold'>Total</dt>
						<dd className='flex items-baseline gap-2'>
							<span className='text-xs text-gray-400'>{isLoyaltyOrder ? 'POINTS' : order.currency}</span>
							<span className='font-semibold'>{isLoyaltyOrder ? totalPoints.toLocaleString() : `$${order.total_price}`}</span>
						</dd>
					</div>
				</dl>
			</div>

			<div className='p-3 border mb-8'>
				<h2 className='text-base font-medium mb-3'>Fulfillment</h2>
				{fulfillmentGroups.length === 0 ? (
					<p className='text-sm text-gray-400'>No fulfillment data.</p>
				) : (
					<div className='space-y-4'>
						{fulfillmentGroups.map((group) => {
							const isCustom = group.type !== 'PHOENIX_STANDARD';
							return (
								<div key={`${group.type}|${group.partnerName || ''}|${group.deliveryMethod || ''}`}>
									<div className='flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-2'>
										<span className='text-sm font-medium'>
											{FULFILLMENT_TYPE_LABELS[group.type] || group.type}
										</span>
										{isCustom && group.partnerName && (
											<span className='text-xs text-gray-500'>{group.partnerName}</span>
										)}
										{isCustom && group.deliveryMethod && (
											<span className='text-xs text-gray-400'>
												{DELIVERY_METHOD_LABELS[group.deliveryMethod] || group.deliveryMethod}
											</span>
										)}
									</div>
									<ul className='text-sm divide-y divide-gray-100 border-t border-gray-100'>
										{group.items.map((item) => (
											<li key={item.order_line_item_id} className='flex items-center justify-between gap-4 py-2'>
												<span>
													{item.title || item.product_number || '-'}
													<span className='text-gray-400'> &times; {item.quantity}</span>
												</span>
												{isCustom && (
													<span className='flex items-center gap-3 shrink-0 text-xs'>
														{item.exported_at && (
															<span className='text-gray-400'>Exported {formatDate(item.exported_at, { pad: false })}</span>
														)}
														{item.custom_form_data?.length ? (
															<button
																type='button'
																className='text-blue-600 hover:underline'
																onClick={() => setFormDetailsItem(item)}
															>
																View details
															</button>
														) : (
															<span className='text-gray-400'>No details submitted</span>
														)}
													</span>
												)}
											</li>
										))}
									</ul>
								</div>
							);
						})}
					</div>
				)}
			</div>

			<div className='grid grid-cols-2 gap-8 mb-8'>
				<div className='p-3 border'>
					<h2 className='text-base font-medium mb-3'>Shipment</h2>
					{status.fulfillment ? (
						<dl className='text-sm space-y-1'>
							<div className='flex justify-between'><dt className='text-gray-500'>Tracking Number</dt><dd>{status.fulfillment.tracking_number || '-'}</dd></div>
							<div className='flex justify-between'><dt className='text-gray-500'>Tracking Link</dt><dd>
								{status.fulfillment.tracking_link ? (
									<a href={status.fulfillment.tracking_link} target='_blank' rel='noreferrer' className='text-blue-600 hover:underline'>
										View tracking
									</a>
								) : '-'}
							</dd></div>
							<div className='flex justify-between'><dt className='text-gray-500'>Shipped Status</dt><dd>{status.fulfillment.shipping_lines_source || '-'}</dd></div>
							<div className='flex justify-between'><dt className='text-gray-500'>Shipped At</dt><dd>{formatDate(status.fulfillment.shipped_at, { pad: false })}</dd></div>
						</dl>
					) : (
						<div className='text-sm text-gray-400'>
							<p>No shipment data exists yet.</p>
						</div>
					)}
				</div>

				<div className='p-3 border'>
					<h2 className='text-base font-medium mb-3'>Pace Submission Details</h2>
					<dl className='text-sm space-y-1'>
						<div className='flex justify-between'><dt className='text-gray-500'>Submitted successfully</dt><dd>{status.pace.submitted ? 'Yes' : 'No'}</dd></div>
						<div className='flex justify-between'><dt className='text-gray-500'>Status</dt><dd>{status.pace.status}</dd></div>
						<div className='flex justify-between'><dt className='text-gray-500'>Pace Order #</dt><dd>{order.erp_order_number || status.pace.job || '-'}</dd></div>
						<div className='flex justify-between'><dt className='text-gray-500'>Attempts</dt><dd>{status.pace.attempt_number}</dd></div>
						<div className='flex justify-between'><dt className='text-gray-500'>Pace Job Status</dt><dd>{status.pace_job_status || '(Pending)'}</dd></div>
					</dl>
				</div>
			</div>

			{isLoyaltyOrder && (
				<div className='grid grid-cols-2 gap-8 mb-8'>
					<div className='p-3 border'>
						<h2 className='text-base font-medium mb-3'>Loyalty Payment</h2>
						{order.loyalty ? (
							<dl className='text-sm space-y-1'>
								<div className='flex justify-between'><dt className='text-gray-500'>Program</dt><dd>{order.loyalty.program_code}</dd></div>
								<div className='flex justify-between'><dt className='text-gray-500'>Points Required</dt><dd>{order.loyalty.points_required}</dd></div>
								<div className='flex justify-between'><dt className='text-gray-500'>Approval</dt><dd>{order.loyalty.approval_status}</dd></div>
							</dl>
						) : (
							<p className='text-sm text-gray-400'>No loyalty payment on this order.</p>
						)}
					</div>
				</div>
			)}

			{needsResend && canResubmitToErp && (
				<>

				<div className='mt-8'>
					<div className='w-full flex items-center justify-between mb-1'>
						<div className='flex items-center gap-2'>
							<Flag className='size-4 text-red-400 shrink-0' />
							<h2 className='text-base font-medium text-red-400'>
								{recheckRequired
									? 'This order has one or more invalid products.'
									: 'The order failed trying to insert into the ERP app.'}
							</h2>
						</div>
						{retryRelevant && resendExpanded && (
							<button
								type='button'
								className='text-xs text-gray-400 hover:text-black underline'
								onClick={() => setResendExpanded(false)}
							>
								Cancel
							</button>
						)}

						{retryRelevant && !resendExpanded && (
							<button type='button' className='btn-primary h-10' onClick={() => setResendExpanded(true)}>
								Retry
							</button>
						)}
					</div>
					{retryRelevant && resendExpanded && (
						<div className='pt-6 space-y-4 max-w-lg'>
							<div className='border border-gray-200 rounded-md p-4'>
								<div className='flex items-center justify-between mb-1'>
									<div className='flex items-center gap-2'>
										<span className='text-sm font-medium'>
											1. Recheck data{!recheckRequired && <span className='text-gray-400 font-normal'> (optional)</span>}
										</span>
										<Tooltip content='Re-checks this order against current product and inventory data. If it now passes, the order is queued to send to ERP.' />
									</div>
									<StepPill status={recheckStatus} />
								</div>

								{step1Collapsed ? (
									<div className='flex items-center justify-between'>
										<p className='text-xs text-gray-400'>Skipped — sending directly.</p>
										<button
											type='button'
											className='text-xs text-gray-400 hover:text-black underline'
											onClick={() => setStep1Collapsed(false)}
										>
											Show
										</button>
									</div>
								) : (
									<>
										<p className='text-xs text-gray-400 mb-3'>
											{recheckRequired
												? 'Fix the problem above first, then recheck here before sending to ERP.'
												: 'Rechecking first is optional but recommended before resending.'}
										</p>
										<form onSubmit={handleRecheck} className='flex items-end gap-3'>
											<div className='flex flex-col flex-1'>
												<label className='text-xs text-gray-500'>What did you fix? (required, for the record)</label>
												<input
													className='form-input'
													required
													value={reason}
													onChange={(e) => setReason(e.target.value)}
												/>
											</div>
											<button type='submit' disabled={recheckStatus === 'working'} className='btn-primary h-10 shrink-0'>
												{recheckStatus === 'working' ? 'Checking...' : 'Recheck'}
											</button>
										</form>
										{recheckMessage && (
											<p className={`text-xs mt-2 ${recheckStatus === 'failed' ? 'text-pi-red' : 'text-green-700'}`}>
												{recheckMessage}
											</p>
										)}
										{!recheckRequired && recheckStatus === 'idle' && (
											<button
												type='button'
												className='text-xs text-gray-400 hover:text-black underline mt-2'
												onClick={() => setStep1Collapsed(true)}
											>
												Skip, just resend
											</button>
										)}
									</>
								)}
							</div>

							<div className={`border rounded-md p-4 ${sendUnlocked ? 'border-gray-200' : 'border-gray-100 bg-gray-50 opacity-60'}`}>
								<div className='flex items-center justify-between mb-1'>
									<div className='flex items-center gap-2'>
										<span className='text-sm font-medium'>2. Send to ERP</span>
										<Tooltip content='Submits this order to ERP right now, instead of waiting for the automatic background check (which runs about once a minute).' />
									</div>
									<StepPill status={sendStatus} />
								</div>
								<p className='text-xs text-gray-400 mb-3'>
									{sendUnlocked ? 'Sends this order to ERP right now.' : 'Complete the recheck above first.'}
								</p>
								<div className='flex items-center gap-3'>
									<button
										type='button'
										disabled={!sendUnlocked || sendStatus === 'working'}
										className='btn-primary h-10'
										onClick={() => handleSend(false)}
									>
										{sendStatus === 'working' ? 'Sending...' : 'Send to ERP now'}
									</button>
									<button
										type='button'
										disabled={!sendUnlocked || sendStatus === 'working'}
										className='text-xs text-gray-400 hover:text-black underline disabled:opacity-50'
										onClick={() => handleSend(true)}
									>
										Just check, don't send
									</button>
								</div>
								{sendMessage && (
									<p className={`text-xs mt-2 ${sendStatus === 'failed' ? 'text-pi-red' : 'text-green-700'}`}>
										{sendMessage}
									</p>
								)}
							</div>
						</div>
					)}
				</div>
				</>
			)}

			<DialogModal open={timelineOpen} setOpen={setTimelineOpen} title='Order Timeline'>
				<div className='max-h-96 overflow-y-auto pr-1'>
					<OrderTimeline events={events} approval={order.approval} />
				</div>
			</DialogModal>

			<DialogModal
				open={!!formDetailsItem}
				setOpen={(open) => !open && setFormDetailsItem(null)}
				title={formDetailsItem?.title || 'Submitted details'}
			>
				<div className='max-h-96 overflow-y-auto pr-1'>
					<dl className='text-sm space-y-1'>
						{(formDetailsItem?.custom_form_data || []).map((field) => (
							<div key={field.name} className='flex justify-between gap-4'>
								<dt className='text-gray-500'>{humanizeFieldName(field.name)}</dt>
								<dd className='text-right max-w-xs break-words'>{field.value || '-'}</dd>
							</div>
						))}
					</dl>
				</div>
			</DialogModal>

			<DialogModal open={approveConfirmOpen} setOpen={setApproveConfirmOpen} title='Approve this order?'>
				<p className='text-sm text-gray-600'>
					Order {order.order_number} moves to Ready for ERP and is sent to Pace on the next submission run. This
					cannot be undone.
				</p>
				<div className='flex justify-end gap-2 pt-2'>
					<button type='button' className='btn-secondary' onClick={() => setApproveConfirmOpen(false)}>
						Back
					</button>
					<button type='button' className='btn-primary' onClick={handleApproveConfirm}>
						Approve Order
					</button>
				</div>
			</DialogModal>

			<DialogModal open={releaseConfirmOpen} setOpen={setReleaseConfirmOpen} title='Release this order to the ERP?'>
				<p className='text-sm text-gray-600'>
					Order {order.order_number} moves to Ready for ERP and is sent to Pace on the next submission run. This
					cannot be undone.
				</p>
				<div className='flex justify-end gap-2 pt-2'>
					<button type='button' className='btn-secondary' onClick={() => setReleaseConfirmOpen(false)}>
						Back
					</button>
					<button type='button' className='btn-primary' onClick={handleReleaseConfirm}>
						Release Order
					</button>
				</div>
			</DialogModal>

			<DialogModal open={cancelConfirmOpen} setOpen={setCancelConfirmOpen} title='Cancel this order?'>
				<p className='text-sm text-gray-600'>This action will mark the order cancelled. This cannot be undone.</p>
				<div className='flex flex-col'>
					<label className='text-xs text-gray-500'>Reason for cancelling</label>
					<textarea
						className='form-input mt-2'
						rows={3}
						value={cancelReason}
						onChange={(e) => setCancelReason(e.target.value)}
					/>
				</div>
				<div className='flex justify-end gap-2 pt-2'>
					<button type='button' className='btn-secondary' onClick={() => setCancelConfirmOpen(false)}>
						Back
					</button>
					<button type='button' className='btn-alert' onClick={handleCancelConfirm}>
						Cancel Order
					</button>
				</div>
			</DialogModal>

			<DialogModal open={jobStatusConfirmOpen} setOpen={setJobStatusConfirmOpen} title='Simulate Pace job status'>
				<p className='text-sm text-gray-600'>
					Set this test-mode order&apos;s simulated job status.</p>
				<div className='flex flex-col'>
					<label className='text-xs text-gray-500'>Job status</label>
					<select
						className='form-input mt-2'
						value={jobStatusValue}
						onChange={(e) => setJobStatusValue(e.target.value)}
					>
						{JOB_STATUS_OPTIONS.map((def) => (
							<option key={def.key} value={def.key} disabled={!isJobStatusOptionSelectable(def.key)}>
								{def.label}
							</option>
						))}
					</select>
				</div>
				<div className='flex justify-end gap-2 pt-2'>
					<button type='button' className='btn-secondary' onClick={() => setJobStatusConfirmOpen(false)}>
						Back
					</button>
					<button
						type='button'
						className='btn-primary'
						disabled={!isJobStatusOptionSelectable(jobStatusValue)}
						onClick={handleJobStatusConfirm}
					>
						Set Job Status
					</button>
				</div>
			</DialogModal>
		</div>
	);
}
